import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Container,
  Backdrop,
  CircularProgress,
  Typography,
  Toolbar,
  Stack,
  Button,
  Paper,
  Grid,
  Link,
  Breadcrumbs,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridToolbar } from '@mui/x-data-grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyIcon from '@mui/icons-material/ContentCopy';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { useUser, useGpuClasses, useLlmConfigs } from '@/providers';
import getLlmTypeLabel from '@/util/getLlmTypeLabel';

import {
  fetchJobs,
  fetchJob,
  updateJob,
  terminateJob,
} from '../../../actions/jobs';
import { clearErrorMessage } from '../../../actions/errorMessage';
import { verifyPublicDatasets } from '../../../actions/publicDatasets';
import { fetchDatasets } from '../../../actions/datasets';
import { fetchProjectDatastores } from '../../../actions/projectDatastores';
import STATUSES from '../../statuses';
import JOB_TYPES from '../types';
import PollEntity from '../../shared/PollEntity';
import toNiceTimestamp from '../../../util/toNiceTimestamp';
import ErrorSnackBar from '../../shared/ErrorSnackBar';
import THIRD_PARTY_TYPES from '../JobForm/thirdPartyTypes';
import ConnectDialog from '../../shared/ConnectDialog';
import getGpuTypeName from '../getGpuTypeName';

const EMPTY_ARRAY = [];

const job_types_with_edit_copy = [
  JOB_TYPES.NOTEBOOK.value,
  JOB_TYPES.LLM.value,
  JOB_TYPES.GUARDRAIL.value,
];

export default function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { type: typeParam } = useParams();

  const type =
    typeParam === 'undefined'
      ? JOB_TYPES.NOTEBOOK
      : Object.entries(JOB_TYPES)
          .map(([key, value]) => {
            return value.value === typeParam ? value : undefined;
          })
          .filter((type) => type)[0] || JOB_TYPES.NOTEBOOK;

  const user = useUser();
  const allJobs = useSelector((state) => state.jobs);
  const gpuClassesRaw = useGpuClasses();
  const gpuClasses = useMemo(() => gpuClassesRaw || [], [gpuClassesRaw]);
  const models = useSelector((state) => state.models ?? EMPTY_ARRAY);
  const api_error = useSelector((state) => state.errorMessage);
  const llm_configs = useLlmConfigs();

  const filtered_jobs = [
    JOB_TYPES.NOTEBOOK,
    JOB_TYPES.ENDPOINT,
    JOB_TYPES.LLM,
    JOB_TYPES.GUARDRAIL,
  ].includes(type)
    ? (allJobs || []).filter((job) => job.type === type.value)
    : (allJobs || [])
        .filter((job) => job.type === type.value)
        .sort((a, b) => {
          return (
            new Date(b.createdAt || b.start) - new Date(a.createdAt || a.start)
          );
        });

  const [selectedJobs, setSelectedJobs] = useState([]);

  // Convert array to DataGrid v8 format: { type: 'include', ids: Set }
  const rowSelectionModel = useMemo(() => {
    if (!selectedJobs || selectedJobs.length === 0) {
      return { type: 'include', ids: new Set() };
    }
    return { type: 'include', ids: new Set(selectedJobs) };
  }, [selectedJobs]);
  const [transitioningJobs, setTransitioningJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectId, setConnectId] = useState(null);
  const [connectEndpoint, setConnectEndpoint] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelingLoading, setCancelingLoading] = useState(false);

  useEffect(() => {
    if (typeParam === 'undefined') {
      navigate('/dashboard');
    }
  }, [typeParam, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearErrorMessage());
    };
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        if (allJobs === null || allJobs === undefined) {
          await Promise.all([
            dispatch(verifyPublicDatasets()),
            user?.selected_project
              ? dispatch(fetchDatasets(user.selected_project))
              : Promise.resolve(),
            user?.selected_project
              ? dispatch(fetchJobs(user.selected_project))
              : Promise.resolve(),
            user?.cloudbender_enabled && user?.selected_project
              ? dispatch(fetchProjectDatastores(user.selected_project))
              : Promise.resolve(),
          ]);
          // GpuClasses are fetched by Layout via context
          return;
        }
        setLoading(false);
        setMounted(true);
      } catch {
        setLoading(false);
        setMounted(true);
      }
    })();
  }, [dispatch, allJobs, user?.cloudbender_enabled, user?.selected_project]);

  const restartJob = useCallback(
    async (job_uuid) => {
      if (user?.selected_project) {
        await dispatch(
          updateJob(user.selected_project, job_uuid, { command: 'start' }),
        );
      }
    },
    [dispatch, user?.selected_project],
  );

  const stopJob = useCallback(
    async (job_uuid) => {
      if (user?.selected_project) {
        await dispatch(
          updateJob(user.selected_project, job_uuid, { command: 'stop' }),
        );
      }
    },
    [dispatch, user?.selected_project],
  );

  const terminateJobAction = useCallback(
    async (job_uuid) => {
      if (user?.selected_project) {
        await dispatch(terminateJob(user.selected_project, job_uuid));
      }
    },
    [dispatch, user?.selected_project],
  );

  const handleRestart = useCallback(async () => {
    const jobs = [...selectedJobs];
    setSelectedJobs([]);
    setTransitioningJobs((prev) => [...prev, ...jobs]);
    for (let i = 0; i < jobs.length; i++) {
      try {
        await restartJob(jobs[i]);
      } catch (error) {
        console.log(error);
      }
    }
    setTransitioningJobs((prev) => prev.filter((job) => !jobs.includes(job)));
  }, [selectedJobs, restartJob]);

  const stopJobs = useCallback(
    async (jobs) => {
      setSelectedJobs([]);
      setTransitioningJobs((prev) => [...prev, ...jobs]);
      for (let i = 0; i < jobs.length; i++) {
        await stopJob(jobs[i]);
      }
      setTransitioningJobs((prev) => prev.filter((job) => !jobs.includes(job)));
    },
    [stopJob],
  );

  const handleStop = useCallback(async () => {
    const selectedStatuses = new Set(
      filtered_jobs
        .filter((job) => selectedJobs.includes(job.job_uuid))
        .map((job) => job.status),
    );
    if (selectedStatuses.has(STATUSES.UPLOADING)) {
      setCancelOpen(true);
    } else {
      await stopJobs(selectedJobs);
    }
  }, [selectedJobs, filtered_jobs, stopJobs]);

  const handleTerminate = useCallback(async () => {
    const jobs = [...selectedJobs];
    setSelectedJobs([]);
    setTransitioningJobs((prev) => [...prev, ...jobs]);
    for (let i = 0; i < jobs.length; i++) {
      await terminateJobAction(jobs[i]);
    }
    setTransitioningJobs((prev) => prev.filter((job) => !jobs.includes(job)));
  }, [selectedJobs, terminateJobAction]);

  const handleOpen = useCallback((job) => {
    const win = window.open(
      `${job.endpoint.url}/?token=${job.endpoint.token}`,
      '_blank',
    );
    if (win != null) {
      win.focus();
    }
  }, []);

  const validMenuOptions = useCallback(() => {
    const selectedStatuses = new Set(
      filtered_jobs
        .filter(
          (job) =>
            selectedJobs.includes(job.job_uuid) &&
            !transitioningJobs.includes(job.job_uuid),
        )
        .map((job) => job.status),
    );
    let options = [];

    if (selectedStatuses.has(STATUSES.RUNNING) && selectedStatuses.size === 1) {
      options.push('stop');
    }
    if (
      selectedStatuses.has(STATUSES.WAITING_GPU) &&
      selectedStatuses.size === 1
    ) {
      options.push('stop');
    }
    if (
      selectedStatuses.has(STATUSES.WAITING_GPU) &&
      selectedStatuses.has(STATUSES.RUNNING) &&
      selectedStatuses.size === 2
    ) {
      options.push('stop');
    }
    if (job_types_with_edit_copy.includes(type.value)) {
      if (selectedStatuses.has(STATUSES.STOPPED) && selectedJobs.length === 1) {
        options.push('edit');
        options.push('copy');
      }
      if (selectedStatuses.has(STATUSES.RUNNING) && selectedJobs.length === 1) {
        options.push('copy');
      }
    }
    if (
      [
        JOB_TYPES.NOTEBOOK.value,
        JOB_TYPES.ENDPOINT.value,
        JOB_TYPES.LLM.value,
        JOB_TYPES.GUARDRAIL.value,
      ].includes(type.value)
    ) {
      if (
        selectedStatuses.has(STATUSES.STOPPED) &&
        selectedStatuses.size === 1
      ) {
        options.push('restart');
      }
    }
    if (
      selectedStatuses.has(STATUSES.UPLOADING) &&
      selectedStatuses.size === 1
    ) {
      options.push('stop');
    }

    if (selectedJobs.length === 1) {
      options.push('rerun');
    }
    if (selectedStatuses.size) {
      options.push('terminate');
    }

    return options;
  }, [selectedJobs, filtered_jobs, transitioningJobs, type]);

  const renderStatus = useCallback(
    (params) => {
      if (transitioningJobs.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        );
      }

      const status = params.row.status;
      if (
        [
          STATUSES.NEW,
          STATUSES.STARTING,
          STATUSES.PROVISIONING,
          STATUSES.STOPPING,
          STATUSES.REMOVING,
          STATUSES.BUILDING,
          STATUSES.DOWNLOADING,
          STATUSES.CACHING,
          STATUSES.RESTORING,
          STATUSES.SAVING,
          STATUSES.UPDATING,
          STATUSES.COPYING,
          STATUSES.COMPRESSING,
          STATUSES.UPLOADING,
        ].includes(status)
      ) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <CircularProgress size={20} />
              <Typography variant='body2'>{status}</Typography>
            </Stack>
            <PollEntity
              poll_function={() =>
                user?.selected_project && params.row.job_uuid
                  ? fetchJob(user.selected_project, params.row.job_uuid)
                  : null
              }
              interval={5000}
            />
          </Box>
        );
      }
      if (
        [
          STATUSES.WAITING_DATA,
          STATUSES.WAITING_RESTORE,
          STATUSES.WAITING_INSTANCE,
          STATUSES.WAITING_GPU,
        ].includes(status)
      ) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <CircularProgress size={20} />
              <Typography variant='body2'>{status}</Typography>
            </Stack>
            <PollEntity
              poll_function={() =>
                user?.selected_project && params.row.job_uuid
                  ? fetchJob(user.selected_project, params.row.job_uuid)
                  : null
              }
              interval={5000}
            />
          </Box>
        );
      }
      if (status === STATUSES.STOPPED) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <StopCircleIcon color='disabled' />
              <Typography variant='body2'>{status}</Typography>
            </Stack>
          </Box>
        );
      }
      if (status === STATUSES.FINISHED) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <CheckCircleIcon color='disabled' />
              <Typography variant='body2'>{status}</Typography>
            </Stack>
          </Box>
        );
      }
      if (status === STATUSES.CANCELED) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <CancelOutlinedIcon color='disabled' />
              <Typography variant='body2'>{status}</Typography>
            </Stack>
          </Box>
        );
      }
      if (status === STATUSES.RUNNING || status === STATUSES.READY) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <CheckCircleIcon color='success' />
              <Typography variant='body2'>{status}</Typography>
            </Stack>
            <PollEntity
              poll_function={() =>
                user?.selected_project && params.row.job_uuid
                  ? fetchJob(user.selected_project, params.row.job_uuid)
                  : null
              }
              interval={30000}
            />
          </Box>
        );
      }
      if (status === STATUSES.FAILED) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <ErrorOutlineIcon color='error' />
              <Typography variant='body2'>{status}</Typography>
            </Stack>
          </Box>
        );
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='body2'>{status}</Typography>
          </Stack>
        </Box>
      );
    },
    [transitioningJobs, user],
  );

  const renderName = useCallback((params) => {
    return (
      <Link
        component={RouterLink}
        to={`/jobs/${params.row.type}/details/${params.row.job_uuid}`}
      >
        {params.row.name}
      </Link>
    );
  }, []);

  const renderCreatedDate = useCallback((params) => {
    return toNiceTimestamp(params.row.createdAt || params.row.start, false);
  }, []);

  const renderGpuType = useCallback(
    (params) => {
      if (
        ![
          JOB_TYPES.NOTEBOOK.value,
          JOB_TYPES.ENDPOINT.value,
          JOB_TYPES.LLM.value,
          JOB_TYPES.GUARDRAIL.value,
        ].includes(params.row.type)
      ) {
        return null;
      }
      return params.row.resources?.gpu_types?.[0]
        ? getGpuTypeName(params.row.resources.gpu_types[0], gpuClasses) ||
            'Unknown'
        : 'Unknown';
    },
    [gpuClasses],
  );

  const renderGpuCount = useCallback((params) => {
    if (
      ![
        JOB_TYPES.NOTEBOOK.value,
        JOB_TYPES.ENDPOINT.value,
        JOB_TYPES.LLM.value,
        JOB_TYPES.GUARDRAIL.value,
      ].includes(params.row.type)
    ) {
      return null;
    }
    return params.row.resources?.gpu_count || 0;
  }, []);

  const renderEnvironment = useCallback((params) => {
    const env = params.row.environment;
    if (env?.status === STATUSES.READY) {
      return env.type === 'CUSTOM' ? env.custom_image : env.type;
    }
    return env?.status || 'Unknown';
  }, []);

  const renderModel = useCallback(
    (params) => {
      const model = params.row.model;
      if (model?.status === STATUSES.READY) {
        if (model?.source_type) {
          if (
            ![
              THIRD_PARTY_TYPES.TRAINML.value,
              THIRD_PARTY_TYPES.EVEFS.value,
            ].includes(model.source_type)
          ) {
            return model.source_uri;
          } else {
            const modelObj = models.find(
              (m) => m.model_uuid === model.source_uri,
            );
            return modelObj ? modelObj.name : 'Unknown';
          }
        }
        return 'None';
      }
      return model?.status || 'Unknown';
    },
    [models],
  );

  const renderLlmType = useCallback((params) => {
    const llm_type = params.row.endpoint?.llm?.type;
    return getLlmTypeLabel(llm_type) || '—';
  }, []);

  const renderLlmFamily = useCallback(
    (params) => {
      const llm_type = params.row.endpoint?.llm?.type;
      const family_key = params.row.endpoint?.llm?.family;
      return (
        llm_configs?.[llm_type]?.[family_key]?.label ?? family_key ?? '—'
      );
    },
    [llm_configs],
  );

  const renderLlmSize = useCallback((params) => {
    return params.row.endpoint?.llm?.size ?? '—';
  }, []);

  const renderData = useCallback((params) => {
    const data = params.row.data;
    if (data?.status === STATUSES.READY) {
      if (params.row.type === JOB_TYPES.INFERENCE.value) {
        return data.input_uri
          ? data.input_type === THIRD_PARTY_TYPES.REGIONAL.value
            ? data.input_options?.path
            : data.input_uri
          : 'None';
      }
      if (
        [JOB_TYPES.NOTEBOOK.value, JOB_TYPES.TRAINING.value].includes(
          params.row.type,
        )
      ) {
        return data?.datasets?.length
          ? data.datasets.map((dataset) => dataset.name).join(', ')
          : 'None';
      }
    }
    return data?.status || 'Unknown';
  }, []);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 2,
        renderCell: renderName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        renderCell: renderStatus,
        flex: 1,
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        type: 'string',
        renderCell: renderCreatedDate,
        flex: 0.5,
      },
    ];

    if (
      [
        JOB_TYPES.NOTEBOOK.value,
        JOB_TYPES.ENDPOINT.value,
        JOB_TYPES.LLM.value,
        JOB_TYPES.GUARDRAIL.value,
      ].includes(type.value)
    ) {
      baseColumns.push(
        {
          field: 'gpu_type',
          headerName: 'GPU Type',
          type: 'string',
          renderCell: renderGpuType,
          flex: 0.5,
        },
        {
          field: 'gpu_count',
          headerName: 'GPU Count',
          type: 'number',
          renderCell: renderGpuCount,
          flex: 0.25,
        },
      );
    }

    if (type.value === JOB_TYPES.LLM.value) {
      baseColumns.push(
        {
          field: 'llm_type',
          headerName: 'Type',
          type: 'string',
          renderCell: renderLlmType,
          valueGetter: (value, row) => row.endpoint?.llm?.type,
          flex: 0.5,
        },
        {
          field: 'llm_family',
          headerName: 'Family',
          type: 'string',
          renderCell: renderLlmFamily,
          valueGetter: (value, row) => row.endpoint?.llm?.family,
          flex: 0.5,
        },
        {
          field: 'llm_size',
          headerName: 'Size',
          type: 'string',
          renderCell: renderLlmSize,
          valueGetter: (value, row) => row.endpoint?.llm?.size,
          flex: 0.5,
        },
      );
    } else if (type.value !== JOB_TYPES.GUARDRAIL.value) {
      baseColumns.push(
        {
          field: 'environment',
          headerName: 'Environment',
          type: 'string',
          renderCell: renderEnvironment,
          flex: 0.5,
          hide: true,
        },
        {
          field: 'model',
          headerName: 'Model',
          type: 'string',
          renderCell: renderModel,
          flex: 0.5,
          hide: true,
        },
      );
    }

    if (
      ![
        JOB_TYPES.ENDPOINT.value,
        JOB_TYPES.LLM.value,
        JOB_TYPES.GUARDRAIL.value,
      ].includes(type.value)
    ) {
      baseColumns.push({
        field: 'data',
        headerName: 'Data',
        type: 'string',
        renderCell: renderData,
        flex: 0.5,
        hide: true,
      });
    }

    baseColumns.push({
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 120,
      getActions: (params) => {
        const actions = [];
        const job = params.row;

        // Connect button for endpoints when running (explicit, in main row)
        if (
          [
            JOB_TYPES.ENDPOINT.value,
            JOB_TYPES.LLM.value,
            JOB_TYPES.GUARDRAIL.value,
          ].includes(job.type) &&
          job.status === STATUSES.RUNNING &&
          job.endpoint &&
          !transitioningJobs.includes(job.job_uuid)
        ) {
          actions.push(
            <GridActionsCellItem
              key='connect'
              icon={
                <Badge color='brand' badgeContent={0}>
                  <LinkOutlinedIcon />
                </Badge>
              }
              label='Connect'
              onClick={() => {
                setConnectId(job.job_uuid);
                setConnectEndpoint(job.endpoint);
                setConnectOpen(true);
              }}
            />,
          );
        }

        // Connect button for training/inference when VPN needed for local data (explicit, in main row)
        if (
          !transitioningJobs.includes(job.job_uuid) &&
          (((job.data?.input_type === THIRD_PARTY_TYPES.LOCAL.value ||
            job.model?.source_type === THIRD_PARTY_TYPES.LOCAL.value) &&
            job.status === STATUSES.WAITING_DATA) ||
            (job.data?.output_type === THIRD_PARTY_TYPES.LOCAL.value &&
              [STATUSES.UPLOADING, STATUSES.RUNNING].includes(job.status)))
        ) {
          actions.push(
            <GridActionsCellItem
              key='connect-vpn'
              icon={
                <Badge color='brand' badgeContent={1}>
                  <LinkOutlinedIcon />
                </Badge>
              }
              label='Connect'
              onClick={() => {
                setConnectId(job.job_uuid);
                setConnectEndpoint(null);
                setConnectOpen(true);
              }}
            />,
          );
        }

        // Open button for running notebooks (explicit, in main row)
        if (
          job.type === JOB_TYPES.NOTEBOOK.value &&
          job.status === STATUSES.RUNNING &&
          !transitioningJobs.includes(job.job_uuid)
        ) {
          actions.push(
            <GridActionsCellItem
              key='open'
              icon={
                <Badge color='brand' badgeContent={'!'}>
                  <OpenInNewIcon />
                </Badge>
              }
              label='Open'
              onClick={() => handleOpen(job)}
            />,
          );
        }

        // View Logs
        if (
          job.type !== JOB_TYPES.NOTEBOOK.value &&
          job.status !== STATUSES.NEW
        ) {
          actions.push(
            <GridActionsCellItem
              key='view-logs'
              icon={<FeedOutlinedIcon />}
              label='View Logs'
              onClick={() => {
                navigate(`/jobs/${job.type}/logs/${job.job_uuid}`);
              }}
              showInMenu
            />,
          );
        }

        // Stop
        if (
          (job.status === STATUSES.RUNNING ||
            job.status === STATUSES.WAITING_GPU ||
            job.status === STATUSES.UPLOADING) &&
          !transitioningJobs.includes(job.job_uuid)
        ) {
          actions.push(
            <GridActionsCellItem
              key='stop'
              icon={<StopCircleIcon />}
              label='Stop'
              onClick={() => {
                if (job.status === STATUSES.UPLOADING) {
                  setSelectedJobs([job.job_uuid]);
                  setCancelOpen(true);
                } else {
                  stopJobs([job.job_uuid]);
                }
              }}
              showInMenu
            />,
          );
        }

        // Restart
        if (
          [
            JOB_TYPES.NOTEBOOK.value,
            JOB_TYPES.ENDPOINT.value,
            JOB_TYPES.LLM.value,
            JOB_TYPES.GUARDRAIL.value,
          ].includes(job.type) &&
          job.status === STATUSES.STOPPED &&
          !transitioningJobs.includes(job.job_uuid)
        ) {
          actions.push(
            <GridActionsCellItem
              key='restart'
              icon={<PlayArrowIcon />}
              label='Restart'
              onClick={() => {
                setTransitioningJobs((prev) => [...prev, job.job_uuid]);
                restartJob(job.job_uuid).finally(() => {
                  setTransitioningJobs((prev) =>
                    prev.filter((id) => id !== job.job_uuid),
                  );
                });
              }}
              showInMenu
            />,
          );
        }

        // Edit (notebooks and LLMs, stopped)
        if (
          job_types_with_edit_copy.includes(job.type) &&
          job.status === STATUSES.STOPPED &&
          !transitioningJobs.includes(job.job_uuid)
        ) {
          actions.push(
            <GridActionsCellItem
              key='edit'
              icon={<EditIcon />}
              label='Edit'
              onClick={() => {
                navigate(`/jobs/${job.type}/edit?id=${job.job_uuid}`);
              }}
              showInMenu
            />,
          );
        }

        // Copy
        if (
          job_types_with_edit_copy.includes(job.type) &&
          (job.status === STATUSES.STOPPED ||
            job.status === STATUSES.RUNNING) &&
          !transitioningJobs.includes(job.job_uuid)
        ) {
          actions.push(
            <GridActionsCellItem
              key='copy'
              icon={<CopyIcon />}
              label='Copy'
              onClick={() => {
                navigate(
                  `/jobs/${job.type}/edit?source_job_uuid=${job.job_uuid}`,
                );
              }}
              showInMenu
            />,
          );
        }

        // Rerun (training/inference)
        if (
          [JOB_TYPES.TRAINING.value, JOB_TYPES.INFERENCE.value].includes(
            job.type,
          ) &&
          !transitioningJobs.includes(job.job_uuid)
        ) {
          actions.push(
            <GridActionsCellItem
              key='rerun'
              icon={<RefreshIcon />}
              label='Rerun'
              onClick={() => {
                navigate(
                  `/jobs/${job.type}/edit?source_job_uuid=${job.job_uuid}`,
                );
              }}
              showInMenu
            />,
          );
        }

        // Terminate
        if (!transitioningJobs.includes(job.job_uuid)) {
          actions.push(
            <GridActionsCellItem
              key='terminate'
              icon={<DeleteIcon />}
              label='Terminate'
              onClick={() => {
                setTransitioningJobs((prev) => [...prev, job.job_uuid]);
                terminateJobAction(job.job_uuid).finally(() => {
                  setTransitioningJobs((prev) =>
                    prev.filter((id) => id !== job.job_uuid),
                  );
                });
              }}
              showInMenu
            />,
          );
        }

        return actions;
      },
    });

    return baseColumns;
  }, [
    renderName,
    renderStatus,
    renderCreatedDate,
    renderGpuType,
    renderGpuCount,
    renderEnvironment,
    renderModel,
    renderLlmType,
    renderLlmFamily,
    renderLlmSize,
    renderData,
    type,
    transitioningJobs,
    navigate,
    handleOpen,
    stopJobs,
    restartJob,
    terminateJobAction,
  ]);

  const validOptions = validMenuOptions();

  if (loading || !mounted) {
    return (
      <Backdrop open={loading || !mounted}>
        <CircularProgress />
      </Backdrop>
    );
  }

  return (
    <Container>
      <Breadcrumbs aria-label='breadcrumb'>
        <Link
          underline='hover'
          color='inherit'
          component={RouterLink}
          to='/dashboard'
        >
          Home
        </Link>
        <Typography color='text.primary'>{type.label}</Typography>
      </Breadcrumbs>
      <br />
      <Paper sx={{ display: 'flex', width: '100%' }}>
        <Grid container spacing={4} sx={{ flexGrow: 1 }}>
          <Grid size={{ xs: 12, md: 12, lg: 12 }}>
            <Paper sx={{ display: 'flex', width: '100%' }} elevation={1}>
              <Toolbar
                sx={{
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 2 },
                  minHeight: { xs: 'auto', sm: '64px' },
                  py: { xs: 1, sm: 0 },
                  px: { xs: 1, sm: 2 },
                }}
              >
                <Typography
                  variant='h4'
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    mb: { xs: 1, sm: 0 },
                    mr: { xs: 0, sm: 2 },
                  }}
                >
                  {type.label}
                </Typography>
                <Stack
                  spacing={1}
                  direction='row'
                  alignItems='center'
                  sx={{ flexWrap: 'wrap', gap: { xs: 1, sm: 1 } }}
                >
                  <Button
                    color='brand'
                    onClick={() => navigate(`/jobs/${type.value}/edit`)}
                    size='small'
                  >
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <AddCircleOutlineIcon />
                      <Typography variant='button' display='block'>
                        Create
                      </Typography>
                    </Stack>
                  </Button>
                  {job_types_with_edit_copy.includes(type.value) && (
                    <Button
                      onClick={() => {
                        const selectedJob = filtered_jobs.find(
                          (job) => job.job_uuid === selectedJobs[0],
                        );
                        if (selectedJob) {
                          navigate(
                            `/jobs/${type.value}/edit?id=${selectedJob.job_uuid}`,
                          );
                        }
                      }}
                      disabled={!validOptions.includes('edit')}
                      size='small'
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <EditIcon />
                        <Typography variant='button' display='block'>
                          Edit
                        </Typography>
                      </Stack>
                    </Button>
                  )}
                  {job_types_with_edit_copy.includes(type.value) && (
                    <Button
                      onClick={() => {
                        const selectedJob = filtered_jobs.find(
                          (job) => job.job_uuid === selectedJobs[0],
                        );
                        if (selectedJob) {
                          navigate(
                            `/jobs/${type.value}/edit?source_job_uuid=${selectedJob.job_uuid}`,
                          );
                        }
                      }}
                      disabled={!validOptions.includes('copy')}
                      size='small'
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <CopyIcon />
                        <Typography variant='button' display='block'>
                          Copy
                        </Typography>
                      </Stack>
                    </Button>
                  )}
                  {[
                    JOB_TYPES.TRAINING.value,
                    JOB_TYPES.INFERENCE.value,
                  ].includes(type.value) && (
                    <Button
                      onClick={() => {
                        const selectedJob = filtered_jobs.find(
                          (job) => job.job_uuid === selectedJobs[0],
                        );
                        if (selectedJob) {
                          navigate(
                            `/jobs/${type.value}/edit?source_job_uuid=${selectedJob.job_uuid}`,
                          );
                        }
                      }}
                      disabled={!validOptions.includes('rerun')}
                      size='small'
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <RefreshIcon />
                        <Typography variant='button' display='block'>
                          Rerun
                        </Typography>
                      </Stack>
                    </Button>
                  )}
                  {[
                    JOB_TYPES.NOTEBOOK.value,
                    JOB_TYPES.ENDPOINT.value,
                    JOB_TYPES.LLM.value,
                    JOB_TYPES.GUARDRAIL.value,
                  ].includes(type.value) && (
                    <Button
                      onClick={handleRestart}
                      disabled={!validOptions.includes('restart')}
                      size='small'
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <PlayArrowIcon />
                        <Typography variant='button' display='block'>
                          Restart
                        </Typography>
                      </Stack>
                    </Button>
                  )}
                  <Button
                    onClick={handleStop}
                    disabled={!validOptions.includes('stop')}
                    size='small'
                  >
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <StopCircleIcon />
                      <Typography variant='button' display='block'>
                        Stop
                      </Typography>
                    </Stack>
                  </Button>
                  <Button
                    onClick={handleTerminate}
                    disabled={!validOptions.includes('terminate')}
                    size='small'
                  >
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <DeleteIcon />
                      <Typography variant='button' display='block'>
                        Terminate
                      </Typography>
                    </Stack>
                  </Button>
                </Stack>
              </Toolbar>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 12 }}>
            <Box
              sx={{
                width: '100%',
              }}
            >
              {filtered_jobs.length ? (
                <DataGrid
                  checkboxSelection
                  getRowId={(row) => row.job_uuid}
                  columns={columns}
                  rows={filtered_jobs}
                  rowSelectionModel={rowSelectionModel}
                  onRowSelectionModelChange={(newSelectionModel) => {
                    // DataGrid v8 returns object format: { type: 'include'|'exclude', ids: Set }
                    if (
                      newSelectionModel &&
                      typeof newSelectionModel === 'object' &&
                      newSelectionModel.ids instanceof Set
                    ) {
                      if (newSelectionModel.type === 'include') {
                        setSelectedJobs(Array.from(newSelectionModel.ids));
                      } else {
                        // exclude mode: all rows except those in ids
                        const excludedIds = Array.from(newSelectionModel.ids);
                        setSelectedJobs(
                          filtered_jobs
                            .map((job) => job.job_uuid)
                            .filter((id) => !excludedIds.includes(id)),
                        );
                      }
                    } else {
                      setSelectedJobs([]);
                    }
                  }}
                  slots={{ toolbar: GridToolbar }}
                  showToolbar
                />
              ) : (
                <Paper sx={{ p: 3 }}>
                  <Typography variant='h6'>
                    You have no active {type.value} jobs
                  </Typography>
                  <Typography variant='body2'>
                    To create one, click the "Create" button.
                  </Typography>
                </Paper>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <ErrorSnackBar message={api_error} />
      <ConnectDialog
        open={connectOpen}
        handleClose={() => setConnectOpen(false)}
        type='job'
        id={connectId}
        endpoint={connectEndpoint}
      />
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle>Cancel Upload</DialogTitle>
        <DialogContent>
          <Typography>
            If you cancel a worker's upload, the worker's output data will be
            permanently deleted. Are you sure you want to cancel?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>No</Button>
          <Button
            color='primary'
            onClick={async () => {
              setCancelingLoading(true);
              await stopJobs(selectedJobs);
              setCancelingLoading(false);
              setCancelOpen(false);
            }}
            disabled={cancelingLoading}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={api_error === 'Not Enough Credits'}
        onClose={() => dispatch(clearErrorMessage())}
      >
        <DialogTitle>Not Enough Credits</DialogTitle>
        <DialogContent>
          <Typography>
            Your account does not have enough credits to start this job. Please
            add credits to your account, reduce the number of GPU for this job,
            or select a lower cost GPU.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dispatch(clearErrorMessage())} color='primary'>
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
